import React, { type ReactElement, type Ref, useCallback, useMemo } from "react";
import cx from "classnames";
import { Flex, type FlexProps } from "@vibe/layout";
import { type AvatarProps } from "../Avatar/Avatar";
import AvatarGroupCounterTooltipContentVirtualizedList from "./AvatarGroupCounterTooltipContentVirtualizedList";
import { avatarRenderer } from "./AvatarGroupCounterTooltipHelper";
import type VibeComponentProps from "../../types/VibeComponentProps";
import { type AvatarType } from "../Avatar/Avatar.types";
import styles from "./AvatarGroupCounterTooltipContent.module.scss";

export interface AvatarGroupCounterTooltipContentProps extends VibeComponentProps {
  /**
   * The type of avatars displayed inside the tooltip.
   */
  type?: AvatarType;
  /**
   * The avatars shown in the tooltip.
   */
  avatars?: ReactElement<AvatarProps>[];
  /**
   * If true, the tooltip uses a virtualized list for performance optimization.
   */
  isVirtualizedList?: boolean;
  /**
   * Ref for the tooltip content container.
   */
  tooltipContentContainerRef?: Ref<HTMLDivElement>;
}

const AvatarGroupCounterTooltipContent: React.FC<AvatarGroupCounterTooltipContentProps> = ({
  avatars = [],
  type,
  className,
  isVirtualizedList = false,
  tooltipContentContainerRef
}) => {
  const getTooltipContent = useCallback((avatarProps: AvatarProps) => {
    return avatarProps?.tooltipProps?.content || avatarProps?.["aria-label"];
  }, []);

  const { avatarItems, displayAsGrid, tooltipContainerAriaLabel } = useMemo(() => {
    const avatarItems = avatars.map(avatar => ({
      value: { ...avatar.props, tooltipContent: getTooltipContent(avatar.props) }
    }));
    const displayAsGrid = !avatarItems.some(item => item.value.tooltipContent);
    const tooltipContainerAriaLabel = !displayAsGrid
      ? avatarItems.map(item => item.value.tooltipContent).join(",")
      : undefined;
    return { avatarItems, displayAsGrid, tooltipContainerAriaLabel };
  }, [avatars, getTooltipContent]);

  const renderedItems = useMemo(
    () => avatarItems.map((item, index) => avatarRenderer(item, index, undefined, type, displayAsGrid)),
    [avatarItems, displayAsGrid, type]
  );

  if (isVirtualizedList) {
    return (
      <AvatarGroupCounterTooltipContentVirtualizedList
        avatarRenderer={avatarRenderer}
        tooltipContentContainerRef={tooltipContentContainerRef}
        tooltipContainerAriaLabel={tooltipContainerAriaLabel}
        avatarItems={avatarItems}
        type={type}
      />
    );
  }

  const flexProps = {
    ref: tooltipContentContainerRef,
    tabIndex: -1,
    role: "treegrid",
    "aria-label": tooltipContainerAriaLabel,
    className: displayAsGrid
      ? cx(styles.scrollableContainer, styles.tooltipContainer, styles.tooltipGridContainer, className)
      : cx(styles.scrollableContainer, styles.tooltipContainer, className),
    direction: displayAsGrid ? "row" : "column",
    gap: displayAsGrid ? "xs" : "small",
    wrap: displayAsGrid
  };

  return <Flex {...(flexProps as FlexProps)}>{renderedItems}</Flex>;
};

export default AvatarGroupCounterTooltipContent;
